'use client';

import React, { useState, useEffect } from 'react';
import { Smartphone, Download, Share, PlusSquare, CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Check if already in standalone PWA mode
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS devices
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIos(isIosDevice);

    if (isIosDevice) {
      setCanInstall(true);
    }

    // Android & Desktop Chrome / Edge beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const triggerInstall = async (): Promise<boolean> => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setCanInstall(false);
        setDeferredPrompt(null);
        return true;
      }
      return false;
    }
    return false;
  };

  return {
    isInstalled,
    canInstall,
    isIos,
    triggerInstall,
  };
}

/**
 * Clean 1-tap Install button for the mobile Profile Sheet or Sidebar.
 */
export function PwaInstallButton({ className = '' }: { className?: string }) {
  const { isInstalled, canInstall, isIos, triggerInstall } = usePwaInstall();
  const [showIosModal, setShowIosModal] = useState(false);
  const [installSuccess, setInstallSuccess] = useState(false);

  if (isInstalled) {
    return null;
  }

  const handleClick = async () => {
    if (isIos) {
      setShowIosModal(true);
      return;
    }

    const installed = await triggerInstall();
    if (installed) {
      setInstallSuccess(true);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`w-full flex items-center justify-between p-3 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/15 border border-emerald-500/25 text-emerald-600 dark:text-emerald-400 transition-colors cursor-pointer group ${className}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="size-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform">
            <Smartphone className="size-5" />
          </div>
          <div className="text-left min-w-0">
            <div className="font-semibold text-xs sm:text-sm text-foreground flex items-center gap-1.5">
              <span>Install WhatsApp CRM App</span>
              <span className="text-[10px] font-bold px-1.5 py-0.2 bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-full">
                PWA
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground truncate">
              {isIos ? 'Add to iPhone Home Screen' : '1-tap install for desktop & mobile'}
            </p>
          </div>
        </div>
        <div className="size-7 rounded-lg bg-emerald-500/15 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
          <Download className="size-4" />
        </div>
      </button>

      {/* iOS Instructions Modal */}
      {showIosModal && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setShowIosModal(false)}
        >
          <div
            className="w-full max-w-sm bg-card rounded-2xl p-5 border border-border space-y-4 text-left shadow-2xl animate-in slide-in-from-bottom-3 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="size-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
                  <Smartphone className="size-4" />
                </div>
                <h3 className="font-bold text-sm text-foreground">Install on iPhone / iPad</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowIosModal(false)}
                className="size-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
              >
                <X className="size-4" />
              </button>
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed">
              Install Zeric WhatsApp CRM as a native app on your iOS home screen:
            </p>

            <div className="space-y-2.5 text-xs text-foreground bg-muted/50 p-3 rounded-xl border border-border/50">
              <div className="flex items-center gap-2.5">
                <div className="size-6 rounded-md bg-background border flex items-center justify-center text-primary shrink-0">
                  <Share className="size-3.5" />
                </div>
                <span>1. Tap the <strong>Share</strong> icon in Safari&apos;s toolbar.</span>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="size-6 rounded-md bg-background border flex items-center justify-center text-primary shrink-0">
                  <PlusSquare className="size-3.5" />
                </div>
                <span>2. Scroll down and select <strong>Add to Home Screen</strong>.</span>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="size-6 rounded-md bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                  <CheckCircle className="size-3.5" />
                </div>
                <span>3. Tap <strong>Add</strong> in the top-right corner.</span>
              </div>
            </div>

            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold"
              onClick={() => setShowIosModal(false)}
            >
              Got It
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
